import { useQueryParams } from 'hooks/useQueryParams';
import { useMemo } from 'react';
import { ItemsCount, Table } from '@spotted-zebra-uk/ui';
import { useCoderbyteWorkspacesQuery } from 'generated/graphql';
import styles from './Coderbyte.module.scss';
import { parseWorkspaceRows, WORKSPACE_COLUMNS } from './Coderbyte.helpers';
import { CoderbyteWorkspaceDrawer } from './CoderbyteWorkspaceDrawer';

/**
 * Every company with a Coderbyte workspace.
 *
 * The open workspace lives in the URL as `?companyId=`, following the soft
 * skills screen, so a drawer can still be linked to and survives a refresh.
 *
 * One row per company in every environment, production included. A
 * non-production environment shows only the companies named in
 * `CODERBYTE_ALLOWED_COMPANY_IDS`, since nothing else can be provisioned
 * there, and their organizations carry the environment in their name.
 */
export function Coderbyte() {
  const [params, setParams] = useQueryParams(['companyId']);
  const openCompanyId = params.companyId ? Number(params.companyId) : null;

  const { data, loading } = useCoderbyteWorkspacesQuery({
    fetchPolicy: 'cache-and-network',
  });

  const workspaces = useMemo(
    () => data?.coderbyteWorkspaces ?? [],
    [data?.coderbyteWorkspaces]
  );
  const rows = useMemo(() => parseWorkspaceRows(workspaces), [workspaces]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Coderbyte</h2>
        <p className={styles.caption}>
          Companies with a Coderbyte workspace. Select one to manage its
          credentials and see its users.
        </p>
      </div>

      {!loading && workspaces.length === 0 ? (
        <p className={styles.emptyState}>
          No company has a Coderbyte workspace yet. Create one from a
          company&apos;s Integrations tab.
        </p>
      ) : (
        <Table
          className={styles.table}
          rows={rows}
          columns={WORKSPACE_COLUMNS}
          loading={loading}
          rowCount={workspaces.length}
          onRowClick={params =>
            setParams({ companyId: String(params.row.companyId) })
          }
          disableColumnFilter
          hasToolbar
          slotProps={{
            toolbar: {
              leftMainAction: (
                <ItemsCount count={workspaces.length} text="Companies" />
              ),
            },
          }}
        />
      )}

      <CoderbyteWorkspaceDrawer
        companyId={openCompanyId}
        onClose={() => setParams({ companyId: null })}
      />
    </div>
  );
}

export default Coderbyte;
